import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { GoogleMap } from '@react-google-maps/api';
import { TerraDraw, TerraDrawPolygonMode, TerraDrawSelectMode } from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import { MapModeToggle, Marker } from '../';
import { getPolygonCenter, parseZoneGeometry, simplifyToVertexBudget, ringSelfIntersects, ringAreaPx } from '../../functions/geoUtils';
// Direct path, not the hooks barrel: the barrel re-exports hooks that pull in
// firebase/auth and firebase/functions, so importing it here drags the whole
// Firebase SDK into this component's dependency graph for one media query.
import { useCoarsePointer } from '../../hooks/useCoarsePointer';
import { colour } from '../../styles/GlobalStyles';
import '../../styles/BuyerZoneMap.css';
import { rasterOnSafari } from '../../utils/mapRendering';

// Saved zones are stored as features of the *polygon* mode, not a render-only
// mode. This is load-bearing: TerraDrawSelectMode resolves its per-feature
// permissions as `flags[feature.properties.mode]`, so a feature whose mode has
// no matching key in the flags object below is silently unselectable — which is
// exactly why map editing never worked when these were rendered as 'static'.
const ZONE_MODE = 'polygon';

// The resting mode. Select mode both renders saved zones and makes them
// tappable; there is no separate display-only mode any more.
const IDLE_MODE = 'select';

// Finger-tracing. Not a TerraDraw mode — Terra Draw's own freehand mode does not
// support touch devices, so this is handled by our own pointer layer and only
// hands the finished ring to TerraDraw/the parent once it is valid.
const FREEHAND_MODE = 'freehand';

// Don't record a point until the finger has travelled this far, in px. Raw
// pointermove fires far denser than anyone can draw meaningfully; this is a
// cheap first pass before RDP does the real work.
const STROKE_MIN_STEP_PX = 3;

// RDP tolerance. 2px keeps the ring visually identical to what was traced while
// typically cutting hundreds of points to tens.
const STROKE_SIMPLIFY_PX = 2;

// Reject accidental taps and tiny scribbles. ~45x45px of screen area.
const STROKE_MIN_AREA_PX = 2000;

// Ceiling on the saved ring's vertex count. RDP normally takes a traced loop
// from ~300 points to ~40, but its tolerance is expressed in the frame the
// shape is measured in — and pinching mid-stroke inflates that frame, which
// silently defeats the simplification (measured: 2 zoom levels of pinch gives
// ~108 points, 8 levels gives ~308, i.e. none removed). Those vertices are
// persisted per zone and walked for every lead during buyer matching, so the
// cost is ongoing rather than one-off. 150 leaves far more detail than a hand
// can trace while keeping the pathological case bounded.
const STROKE_MAX_VERTICES = 150;

// Place lookup runs through our own backend (functions/geocodePlace.js), not the
// Maps JS Geocoder — the geocoder we pay for is Geocodio and its key can't ship
// to a browser. Mirrors the emulator-then-production fallback the public buyer
// wizard already uses, so a local dev without the emulator still gets a working
// search box instead of a dead one.
const GEOCODE_PRODUCTION_URL = 'https://api.clozers.co/v1/geocode';
const GEOCODE_EMULATOR_URL = 'http://localhost:5001/clozers/us-central1/geocodePlace';
const isLocalhost = typeof window !== 'undefined'
    && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

async function geocodeFetch(query) {
    const qs = `?q=${encodeURIComponent(query)}`;
    if (!isLocalhost) return fetch(`${GEOCODE_PRODUCTION_URL}${qs}`);
    try {
        return await fetch(`${GEOCODE_EMULATOR_URL}${qs}`);
    } catch {
        return fetch(`${GEOCODE_PRODUCTION_URL}${qs}`);
    }
}

// Bounds for the pinch we drive ourselves. Google clamps its own gestures but
// not moveCamera, which will happily accept a zoom past the tile range and
// leave the user staring at grey.
const MIN_PINCH_ZOOM = 3;
const MAX_PINCH_ZOOM = 20;

// Stable-enough identity for a zone, used to tell "drawn in this session" from
// "already saved" when offering Undo.
//
// `_key` is stamped by whichever surfaces seed through
// normalizeZonesForEditing — BuyBoxForm, BuyBoxEditForm, and (via Buyers /
// BuyerDetail) BuyerGeographyModal. BuyerSignup and CoLivingBuyerWizard still
// append the raw polygon, and a zone just drawn on the map has no `_key` at all
// until it round-trips through a save. Relying on `_key` alone would therefore
// still hide Undo, so fall back to a geometry fingerprint.
//
// The two schemes coexist safely: they can't collide (`p…` vs `g:…`), and a
// seeded zone keeps its `_key` through an edit because the edit handlers spread
// the existing polygon. Where a zone does fall back to the fingerprint, editing
// it changes that fingerprint — which only costs us the Undo offer, it never
// mis-targets.
function zoneIdentity(poly) {
    if (poly?._key) return poly._key;
    const ring = poly?.geometry?.coordinates?.[0];
    if (!Array.isArray(ring) || ring.length === 0) return null;
    const first = ring[0];
    const last = ring[ring.length - 1];
    const mid = ring[Math.floor(ring.length / 2)];
    return `g:${ring.length}:${first?.[0]},${first?.[1]}:${mid?.[0]},${mid?.[1]}:${last?.[0]},${last?.[1]}`;
}

// Must match the adapter's coordinatePrecision below. TerraDraw validates every
// added feature against this and REJECTS coordinates with more decimal places
// ("Feature has coordinates with excessive precision"). Freehand points come
// straight from fromContainerPixelToLatLng at full float precision (~13dp), so
// they must be rounded or the zone is silently dropped from the map — it would
// still save and still show its chip and badge, just never draw. 6dp is ~0.1m.
const COORDINATE_PRECISION = 6;
const PRECISION_FACTOR = 10 ** COORDINATE_PRECISION;
const roundCoord = (n) => Math.round(n * PRECISION_FACTOR) / PRECISION_FACTOR;

// Build a LatLngBounds from a single polygon's outer ring. Shared by the
// initial "fit all zones" pass and the tap-to-focus pan so both frame zones
// the same way. Returns null when the geometry has no usable coordinates.
function boundsForPolygon(poly) {
    if (!poly?.geometry?.coordinates?.[0]) return null;
    const bounds = new window.google.maps.LatLngBounds();
    let hasCoords = false;
    poly.geometry.coordinates[0].forEach((coord) => {
        bounds.extend({ lat: coord[1], lng: coord[0] });
        hasCoords = true;
    });
    return hasCoords ? bounds : null;
}

function loadExistingPolygons(draw, polygons) {
    if (!polygons || polygons.length === 0) return;

    const features = [];
    polygons.forEach((poly, index) => {
        // Zones are STORED with geometry stringified; callers are expected to seed
        // through normalizeZonesForEditing, but parse defensively here too — a raw
        // stored zone would otherwise fail the coordinates check below and vanish
        // from the map with no error.
        const geometry = parseZoneGeometry(poly.geometry);
        if (!geometry?.coordinates?.[0]) return;
        features.push({
            type: 'Feature',
            properties: {
                mode: ZONE_MODE,
                polygonIndex: index,
            },
            geometry
        });
    });

    if (features.length > 0) {
        try {
            // addFeatures REPORTS invalid geometry in its return value rather
            // than throwing — a rejected zone is dropped silently and simply
            // never draws. Surface it, or the next precision/closure bug costs
            // another debugging session.
            const validations = draw.addFeatures(features);
            const rejected = (Array.isArray(validations) ? validations : [])
                .filter((v) => v && v.valid === false);
            if (rejected.length > 0) {
                console.error(
                    `[BuyerZoneDrawMap] TerraDraw rejected ${rejected.length} of ${features.length} zone(s):`,
                    rejected.map((v) => v.reason)
                );
            }
        } catch (err) {
            console.error('Error loading existing polygons into TerraDraw:', err);
        }
    }
}

function BuyerZoneDrawMap({
    isMapLoaded,
    polygons = [],
    onPolygonAdd,
    onPolygonRemove,
    onPolygonEdit,
    hoveredZoneIndex = null,
    focusedZoneIndex = null,
    onZoneHover,
    // True when this component's container is actually on screen. Callers
    // that keep it permanently mounted behind a CSS `hidden` toggle (so
    // in-progress drawing state survives e.g. a tab switch) must pass this;
    // it defaults to true for callers that only ever mount this while visible.
    isVisible = true,
}) {
    const [rawMap, setRawMap] = useState(null);
    const [mapType, setMapType] = useState('roadmap');
    const [drawingMode, setDrawingMode] = useState(IDLE_MODE);
    const coarsePointer = useCoarsePointer();
    const onPolygonAddRef = useRef(onPolygonAdd);
    const onPolygonRemoveRef = useRef(onPolygonRemove);
    const onPolygonEditRef = useRef(onPolygonEdit);
    const drawRef = useRef(null);
    const polygonsLengthRef = useRef(polygons.length);
    const zoneFeatureIndexRef = useRef({});
    const isSyncingRef = useRef(false);
    // Live count for naming a freshly finished zone. Can't be derived from the
    // draw snapshot any more: saved zones and the just-drawn one now share the
    // same feature mode, so counting the snapshot would be off by one.
    const polygonsCountRef = useRef(polygons.length);
    polygonsCountRef.current = polygons.length;
    // The init effect only runs on rawMap, and its work is deferred to the map's
    // 'idle' event. Zones seeded by the parent in between (Firestore resolving
    // after the map mounted) would be invisible to a captured `polygons`, so the
    // idle callback reads through this ref instead.
    const polygonsRef = useRef(polygons);
    polygonsRef.current = polygons;
    // Latched once the viewport has been framed around saved zones, so nothing
    // later yanks it away. Declared here because both the fit-bounds effect and
    // the geolocation callback need it.
    const hasFitBoundsRef = useRef(false);
    // TerraDraw is built once (the init effect keys on rawMap only), so mode
    // styling reads pointer type through a ref rather than closing over the
    // state value at construction time.
    const coarsePointerRef = useRef(coarsePointer);
    coarsePointerRef.current = coarsePointer;

    // --- Freehand tracing state ---------------------------------------------
    // Points live in a ref, not state: pointermove fires far too often to drive
    // React re-renders. Only the rendered SVG path is state.
    //
    // Each point carries THREE representations, and they are not redundant:
    //   lat/lng — authoritative, what actually gets saved. Captured through the
    //             OverlayView projection, which is the supported conversion and
    //             stays right under padding and device pixel ratio.
    //   wx/wy   — Web Mercator world coordinates, which are zoom- and
    //             camera-independent. Preview only: re-rendering the SVG during
    //             a pinch is then pure arithmetic, with no per-point API call.
    //   x/y     — current screen position, recomputed from wx/wy whenever the
    //             camera moves. Feeds the screen-space geometry checks.
    //
    // Storing geography rather than pixels is what makes pinch-mid-stroke
    // possible at all: pixels silently become wrong the moment the camera moves.
    const strokePointsRef = useRef([]);
    // pointerId → { x, y }. A Map, not a Set, because the pinch needs both live
    // pointer positions, not just how many there are.
    const activePointersRef = useRef(new Map());
    // Non-null while two fingers are down. Freezes the tracing hand and holds
    // everything the camera math needs from the moment the pinch began.
    const pinchRef = useRef(null);
    // Which pointer owns the current stroke. Only it may extend the line, and it
    // transfers to the survivor when a pinch drops back to one finger.
    const strokeOwnerRef = useRef(null);
    const [strokePath, setStrokePath] = useState('');
    const [strokeError, setStrokeError] = useState('');
    const projectionOverlayRef = useRef(null);

    // --- Place search --------------------------------------------------------
    const [searchQuery, setSearchQuery] = useState('');
    const [searching, setSearching] = useState(false);
    // Kept separate from strokeError. Routing search failures through the
    // drawing pill put the message at the BOTTOM of the map — nowhere near the
    // box that produced it, and on top of the map-type toggle, which that pill
    // only ever used to cover while drawing had already dimmed it.
    const [searchError, setSearchError] = useState('');

    // A bare OverlayView, mounted purely to borrow its MapCanvasProjection —
    // that is the only supported way to turn container pixels into lat/lng, and
    // it stays correct under zoom, device pixel ratio and map padding.
    useEffect(() => {
        if (!rawMap || !window.google) return;
        const overlay = new window.google.maps.OverlayView();
        overlay.onAdd = () => {};
        overlay.draw = () => {};
        overlay.onRemove = () => {};
        overlay.setMap(rawMap);
        projectionOverlayRef.current = overlay;
        return () => {
            overlay.setMap(null);
            projectionOverlayRef.current = null;
        };
    }, [rawMap]);

    // MUST stay dependency-free. @react-google-maps/api only re-applies `options`
    // when the object identity changes, and freehand drawing turns Google's own
    // gestureHandling off imperatively via setOptions. Give this memo a
    // dependency and any re-render would silently restore map panning mid-stroke,
    // with no local symptom to trace it back to.
    const mapOptions = useMemo(() => ({
        disableDefaultUI: false,
        scrollwheel: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        keyboardShortcuts: false,
        // Keep the camera flat and north-up. `mapId` makes this a VECTOR map,
        // which can tilt and rotate — and the freehand reprojection is plain
        // 2D Mercator arithmetic, so a tilted or rotated camera would make the
        // preview skew away from where the finger actually drew. Saved
        // coordinates would still be right (they come from the projection), but
        // the line the user watched would not match them.
        //
        // The two *InteractionEnabled flags are the load-bearing ones: `tilt`
        // and `heading` only set the opening camera, and `rotateControl` only
        // removes a widget — none of them stop a two-finger rotate before
        // drawing starts. startFreehand re-flattens as a backstop, for builds
        // where these options are ignored.
        tilt: 0,
        heading: 0,
        rotateControl: false,
        tiltInteractionEnabled: false,
        headingInteractionEnabled: false,
        mapId: JSON.parse(process.env.REACT_APP_COMPS_MAP_ID).key,
        // Safari can't hold a vector map's WebGL context — src/utils/mapRendering.js.
        ...rasterOnSafari()
    }), []);

    const defaultCenter = useMemo(() => ({ lat: 39.8283, lng: -98.5795 }), []);

    useEffect(() => { onPolygonAddRef.current = onPolygonAdd; }, [onPolygonAdd]);
    useEffect(() => { onPolygonRemoveRef.current = onPolygonRemove; }, [onPolygonRemove]);
    useEffect(() => { onPolygonEditRef.current = onPolygonEdit; }, [onPolygonEdit]);

    const handleMapLoad = useCallback((mapInstance) => {
        setRawMap(mapInstance);
        // Read zones through the ref, not the captured prop: this callback is
        // created on first render, when an async seed from Firestore has not
        // landed yet, so the captured array is empty for every returning user.
        if (polygonsRef.current.length > 0 || !navigator.geolocation) return;

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                // The permission prompt can outlive the zone fetch by seconds.
                // If zones arrived and were framed while we waited, centring on
                // the user's location would throw their zones off-screen — and
                // the fit is one-shot, so nothing would bring them back.
                if (hasFitBoundsRef.current || polygonsRef.current.length > 0) return;
                mapInstance.panTo({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                mapInstance.setZoom(12);
            },
            () => {}
        );
    }, []);

    // Maps TerraDraw feature ids → their index in the `polygons` prop. The
    // polygonIndex check also filters out the in-progress shape while drawing,
    // which shares ZONE_MODE but carries no index until it is saved.
    const syncZoneFeatureIndexMap = useCallback((drawInstance) => {
        if (!drawInstance) return;
        try {
            const nextMap = {};
            drawInstance.getSnapshot()
                .filter((f) => f.properties?.mode === ZONE_MODE && typeof f.properties?.polygonIndex === 'number')
                .forEach((f) => {
                    nextMap[f.id] = f.properties.polygonIndex;
                });
            zoneFeatureIndexRef.current = nextMap;
        } catch {
            zoneFeatureIndexRef.current = {};
        }
    }, []);

    const toggleSatellite = useCallback(() => {
        if (rawMap) {
            const newMapType = mapType === 'roadmap' ? 'satellite' : 'roadmap';
            rawMap.setMapTypeId(newMapType);
            setMapType(newMapType);
        }
    }, [rawMap, mapType]);

    // Google Maps reads the container's pixel size at construction time to
    // lay out tiles. A caller that keeps this component mounted behind
    // `hidden` (see the `isVisible` prop) constructs it while the container
    // is 0×0, and Google never notices a later `display:none` → visible flip
    // on its own — it needs an explicit 'resize' event once the container
    // actually has real size, or the map renders blank/mis-tiled. A resize
    // trigger alone commonly leaves the map's pixel-to-LatLng anchoring
    // stale from when the container was 0×0 (a well-known Maps JS quirk for
    // maps embedded in tabs/accordions) — re-applying the current center
    // forces it to recompute anchoring against the now-correct dimensions.
    // Covers buyers with no saved zones yet, where fitBounds (below) never
    // runs to do this itself.
    useEffect(() => {
        if (!isVisible || !rawMap || !window.google) return;
        window.google.maps.event.trigger(rawMap, 'resize');
        const center = rawMap.getCenter();
        if (center) rawMap.setCenter(center);
    }, [isVisible, rawMap]);

    // Initialize Terra Draw after map DOM is fully ready (idle event)
    useEffect(() => {
        if (!rawMap || !window.google) return;

        let draw = null;
        let cancelled = false;

        // Wait for the map's 'idle' event so the DOM elements exist
        const idleListener = rawMap.addListener('idle', () => {
            if (cancelled || drawRef.current) return;
            // Remove the listener — we only need it once
            window.google.maps.event.removeListener(idleListener);

            draw = new TerraDraw({
                adapter: new TerraDrawGoogleMapsAdapter({
                    lib: window.google.maps,
                    map: rawMap,
                    coordinatePrecision: COORDINATE_PRECISION
                }),
                modes: [
                    new TerraDrawPolygonMode({
                        // Saved zones live in this mode, so its styling is what
                        // the user sees at rest — not just mid-draw.
                        styles: {
                            fillColor: colour.jade02,
                            fillOpacity: 0.3,
                            outlineColor: colour.jade03,
                            outlineWidth: 2,
                            closingPointColor: colour.jade03,
                            closingPointOutlineColor: colour.gray00,
                            // The tap-the-first-point-to-close target. Bigger on
                            // a finger; unchanged on a mouse.
                            closingPointWidth: coarsePointerRef.current ? 10 : 6,
                            closingPointOutlineWidth: 2
                        }
                    }),
                    new TerraDrawSelectMode({
                        // Keyed by feature.properties.mode — must match ZONE_MODE
                        // or saved zones are not selectable. See ZONE_MODE above.
                        flags: {
                            [ZONE_MODE]: {
                                feature: {
                                    // Deliberately NOT draggable. With the whole
                                    // feature draggable, any drag starting inside
                                    // a selected zone relocates it wholesale —
                                    // trivially easy to do by accident on touch,
                                    // silently persisted, and with no undo. Users
                                    // reshape zones by their vertices; they never
                                    // ask to slide one bodily across the map.
                                    draggable: false,
                                    coordinates: {
                                        // Object form, not `true`. The bare
                                        // boolean only *renders* midpoints —
                                        // the drag-to-insert-a-vertex branch is
                                        // gated on the object shape, so `true`
                                        // gives handles that look interactive
                                        // and do nothing.
                                        midpoints: { draggable: true },
                                        draggable: true,
                                        deletable: true
                                    }
                                }
                            }
                        },
                        styles: {
                            // NB: selectedPolygonColor, not ...FillColor — the
                            // latter isn't a TerraDraw style key and is silently
                            // ignored, leaving the selected fill default blue.
                            selectedPolygonColor: colour.jade02,
                            selectedPolygonFillOpacity: 0.4,
                            selectedPolygonOutlineColor: colour.jade03,
                            selectedPolygonOutlineWidth: 3,
                            // Vertex and midpoint handles. Terra Draw's own
                            // pointerDistance default (40px) already exceeds
                            // Mapbox's 25px touch buffer, so only the *visual*
                            // handle needs enlarging for fingers.
                            selectionPointWidth: coarsePointerRef.current ? 9 : 6,
                            midPointWidth: coarsePointerRef.current ? 7 : 4,
                            selectionPointColor: colour.gray00,
                            selectionPointOutlineColor: colour.jade03,
                            midPointColor: colour.gray00,
                            midPointOutlineColor: colour.jade03
                        }
                    })
                ]
            });

            draw.start();
            draw.setMode(IDLE_MODE);
            drawRef.current = draw;

            // Load existing polygons — current ones, not those captured when the
            // effect was set up. Keep polygonsLengthRef in step or the sync
            // effect below will think it has already handled this set.
            loadExistingPolygons(draw, polygonsRef.current);
            polygonsLengthRef.current = polygonsRef.current.length;
            syncZoneFeatureIndexMap(draw);

            // Listen for polygon completion — auto-save without prompting for a name.
            //
            // The context check is load-bearing. TerraDraw fires a single 'finish'
            // event for every mode, and select mode fires it at the end of a
            // vertex or feature drag too (action: dragCoordinate / dragFeature /
            // dragCoordinateResize). Without this guard, nudging one vertex of a
            // saved zone would append a duplicate of that zone — and, because the
            // handler then removes the drawn feature, silently swap the original
            // for the copy. Only a genuine draw completion may create a zone.
            draw.on('finish', (id, context) => {
                if (context?.action !== 'draw') return;
                try {
                    const feature = draw.getSnapshotFeature(id);
                    if (feature?.geometry) {
                        // Name from the saved-zone count, not the draw snapshot:
                        // the just-finished shape now shares ZONE_MODE with the
                        // saved ones, so a snapshot count would double-count it.
                        const name = `Zone ${polygonsCountRef.current + 1}`;
                        const geometry = JSON.parse(JSON.stringify(feature.geometry));
                        onPolygonAddRef.current({ name, active: true, geometry });
                        // Claimed on the next render, once the parent has
                        // stamped the new polygon with its _key.
                        pendingAddRef.current = true;
                    }
                    // Drop the transient draw feature — the polygons prop is the
                    // source of truth and the sync effect re-adds it with its index.
                    isSyncingRef.current = true;
                    draw.removeFeatures([id]);
                    isSyncingRef.current = false;
                } catch (err) {
                    isSyncingRef.current = false;
                    console.error('[BuyerZoneDrawMap] Error auto-saving zone:', err);
                }
                draw.setMode(IDLE_MODE);
                setDrawingMode(IDLE_MODE);
            });

            // Listen for geometry updates/deletes in select mode
            draw.on('change', (ids, type) => {
                if (!Array.isArray(ids) || ids.length === 0) {
                    syncZoneFeatureIndexMap(draw);
                    return;
                }

                if (type === 'delete') {
                    // Skip delete events triggered by the polygon sync effect
                    // (which removes and re-adds zone features to refresh them)
                    if (isSyncingRef.current) {
                        ids.forEach((id) => {
                            delete zoneFeatureIndexRef.current[id];
                        });
                        return;
                    }
                    const indexesToRemove = ids
                        .map((id) => zoneFeatureIndexRef.current[id])
                        .filter((idx) => Number.isInteger(idx));
                    if (indexesToRemove.length > 0 && onPolygonRemoveRef.current) {
                        [...new Set(indexesToRemove)]
                            .sort((a, b) => b - a)
                            .forEach((idx) => onPolygonRemoveRef.current(idx));
                    }
                    ids.forEach((id) => {
                        delete zoneFeatureIndexRef.current[id];
                    });
                    return;
                }

                if (type === 'update' && onPolygonEditRef.current) {
                    ids.forEach((id) => {
                        const feature = draw.getSnapshotFeature(id);
                        if (!feature || feature.properties?.mode !== ZONE_MODE) return;
                        // No polygonIndex means this is the shape currently being
                        // drawn, not a saved zone — nothing to write back yet.
                        const polygonIndex = feature.properties?.polygonIndex;
                        if (!Number.isInteger(polygonIndex) || !feature.geometry) return;
                        onPolygonEditRef.current(polygonIndex, JSON.parse(JSON.stringify(feature.geometry)));
                    });
                }

                syncZoneFeatureIndexMap(draw);
            });
        });

        return () => {
            cancelled = true;
            window.google.maps.event.removeListener(idleListener);
            if (draw) {
                draw.stop();
            }
            drawRef.current = null;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawMap]);

    // Fit map bounds to existing polygons on load.
    //
    // Runs on the first render where zones are actually present, not just on
    // rawMap: when the parent seeds zones asynchronously they arrive after the
    // map, and keying on rawMap alone left the user staring at the zoom-4 view
    // of the whole US with their zones somewhere off-screen. The ref makes it
    // fire once, so it never yanks the viewport back mid-session.
    //
    // Also waits on `isVisible`: fitBounds computes zoom from the container's
    // CURRENT pixel size, so calling it while a `hidden` container is 0×0
    // would frame against zero dimensions — wait until the tab is actually
    // shown, not just mounted. No need to re-trigger 'resize' here too: React
    // runs a component's effects in declaration order within a commit, so
    // the resize effect above (same [isVisible, rawMap] dependencies) has
    // already run by the time this one does, on every commit where both
    // could fire.
    useEffect(() => {
        if (!isVisible || !rawMap || !polygons || polygons.length === 0 || !window.google) return;
        if (hasFitBoundsRef.current) return;
        hasFitBoundsRef.current = true;

        const bounds = new window.google.maps.LatLngBounds();
        let hasCoords = false;

        polygons.forEach(poly => {
            if (!poly.geometry?.coordinates?.[0]) return;
            poly.geometry.coordinates[0].forEach(coord => {
                bounds.extend({ lat: coord[1], lng: coord[0] });
                hasCoords = true;
            });
        });

        if (hasCoords) {
            rawMap.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
        } else {
            // Nothing usable to frame — let a later, valid set try again.
            hasFitBoundsRef.current = false;
        }
    }, [isVisible, rawMap, polygons]);

    // Jump the map to a city/ZIP.
    //
    // Without this the only route off the zoom-4 view of North America was the
    // browser geolocation prompt — which, on a public signup page shown to a
    // stranger, most people decline. They were then left pinching from
    // continental scale to their own street before drawing meant anything.
    //
    // Goes through OUR backend rather than google.maps.Geocoder. The geocoder we
    // actually pay for is Geocodio, and it authenticates with a secret that
    // cannot ship to a browser — so /v1/geocode proxies it (functions/
    // geocodePlace.js). That also keeps this off the Maps JS key entirely, which
    // is what made the client-side version fail: the key is referer-restricted
    // and not cleared for the Geocoding API.
    //
    // Geocodio returns a POINT, not a viewport, so the backend infers a zoom
    // from the match precision and we pan rather than fitBounds.
    const handleSearch = useCallback(async (event) => {
        event.preventDefault();
        const address = searchQuery.trim();
        if (!address || !rawMap) return;
        // The Go button is disabled while in flight, but Enter in the input
        // still submits — holding it fires overlapping requests and burns the
        // user's own rate-limit allowance until they get told to slow down.
        if (searching) return;

        setSearching(true);
        try {
            const res = await geocodeFetch(address);

            if (!res.ok) {
                // Each of these is a different thing to tell the user. Collapsing
                // them into one message is what made a permissions outage read as
                // the user mistyping their own city.
                if (res.status === 404) {
                    setSearchError("Couldn't find that place — try a city or ZIP.");
                } else if (res.status === 429) {
                    setSearchError('Too many searches — wait a moment and try again.');
                } else {
                    setSearchError('Search is unavailable — pan and zoom instead.');
                    console.error('Zone map place search failed', res.status);
                }
                return;
            }

            const data = await res.json();
            if (!Number.isFinite(data?.lat) || !Number.isFinite(data?.lng)) {
                setSearchError("Couldn't find that place — try a city or ZIP.");
                return;
            }

            // Claim the one-shot fit before moving, so a zone fetch that lands
            // afterwards can't yank the viewport back off the searched area.
            hasFitBoundsRef.current = true;
            rawMap.panTo({ lat: data.lat, lng: data.lng });
            rawMap.setZoom(Number.isFinite(data.zoom) ? data.zoom : 11);
            setSearchError('');
        } catch (err) {
            // Network-level failure only — HTTP errors are handled above.
            setSearchError('Search is unavailable — pan and zoom instead.');
            console.error('Zone map place search failed', err);
        } finally {
            setSearching(false);
        }
    }, [searchQuery, rawMap, searching]);

    // Tap-to-focus: when a chip is tapped, frame its zone so the user can see
    // exactly which shape it is. Keyed on the focused index (not polygons) so
    // editing a shape mid-session doesn't yank the viewport around.
    useEffect(() => {
        if (focusedZoneIndex == null || !rawMap) return;
        const bounds = boundsForPolygon(polygons[focusedZoneIndex]);
        if (bounds) {
            rawMap.fitBounds(bounds, { top: 80, right: 80, bottom: 80, left: 80 });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusedZoneIndex, rawMap]);

    // Re-sync static features when polygons change externally
    useEffect(() => {
        if (!drawRef.current) return;
        // Skip on initial mount (already loaded in init effect)
        if (polygonsLengthRef.current === polygons.length && polygons.length > 0) return;
        polygonsLengthRef.current = polygons.length;

        const draw = drawRef.current;

        // Clear existing static features (flag prevents change handler from
        // calling onPolygonRemove for these internal removals)
        isSyncingRef.current = true;
        try {
            const snapshot = draw.getSnapshot();
            // Only saved zones (those carrying an index) — never the shape
            // currently being drawn, which shares ZONE_MODE but has no index.
            const zoneIds = snapshot
                .filter(f => f.properties?.mode === ZONE_MODE && typeof f.properties?.polygonIndex === 'number')
                .map(f => f.id);
            if (zoneIds.length > 0) {
                draw.removeFeatures(zoneIds);
            }
        } catch (err) {
            // Ignore errors during cleanup
        }
        isSyncingRef.current = false;

        // Re-add current polygons
        loadExistingPolygons(draw, polygons);
        syncZoneFeatureIndexMap(draw);
    }, [polygons, syncZoneFeatureIndexMap]);

    const startDrawing = useCallback(() => {
        if (!drawRef.current || !rawMap) return;
        drawRef.current.setMode('polygon');
        setDrawingMode('polygon');
    }, [rawMap]);

    const cancelDrawing = useCallback(() => {
        if (!drawRef.current) return;
        drawRef.current.setMode(IDLE_MODE);
        setDrawingMode(IDLE_MODE);
    }, []);

    // TerraDraw exposes no public finish()/cancel() — polygon mode only listens
    // for keyboard (default: Enter finishes, Escape cancels), and the adapter
    // binds those on the map's own div. Buttons therefore synthesise the key.
    // Without this, the *only* way to close a shape is tapping the first vertex
    // again, which is the single worst touch target in the flow.
    const dispatchDrawKey = useCallback((key) => {
        if (!rawMap) return false;
        const target = rawMap.getDiv();
        if (!target) return false;
        target.dispatchEvent(new KeyboardEvent('keyup', { key, bubbles: true }));
        return true;
    }, [rawMap]);

    // Polygon mode silently refuses to close a ring with fewer than 3 real
    // corners. Rather than let Done look broken, say why. Checked on press
    // instead of disabling the button, because vertex placement fires no React
    // state change and a disabled button would never re-enable itself.
    const finishDrawing = useCallback(() => {
        const draw = drawRef.current;
        let ringLength = 0;
        try {
            const inProgress = draw?.getSnapshot().find((f) => (
                f.properties?.mode === ZONE_MODE && typeof f.properties?.polygonIndex !== 'number'
            ));
            ringLength = inProgress?.geometry?.coordinates?.[0]?.length ?? 0;
        } catch {
            ringLength = 0;
        }

        // Terra Draw's in-progress ring carries a closing point and a live
        // cursor vertex on top of the placed corners, so 3 real corners is the
        // point at which the ring reaches 5 entries.
        if (ringLength < 5) {
            setStrokeError('Place at least 3 corners before finishing.');
            return;
        }
        // The 'finish' listener resets the mode and saves the zone.
        dispatchDrawKey('Enter');
    }, [dispatchDrawKey]);

    // Whole-zone undo, not per-vertex. Terra Draw's polygon mode has no
    // vertex-level undo to hook into, and freehand strokes make per-point undo
    // meaningless anyway — popping the last completed zone is the granularity
    // that matches how people actually correct a drawing.
    //
    // Scoped to zones drawn in THIS session. Undo is a reversal of something the
    // user just did; offering it to a returning user whose zones were saved
    // weeks ago turns it into an unlabelled delete button with no confirmation
    // and no redo.
    //
    // Tracked by identity, NOT by a count. Zones can also be deleted from the
    // chip's X in BuyBoxForm and from the map itself, neither of which routes
    // through here — so a counter drifts, and "remove the last one" would then
    // land on a pre-existing zone and destroy it.
    const pendingAddRef = useRef(false);
    const sessionKeysRef = useRef(new Set());
    const knownIdentitiesRef = useRef(new Set());
    const [sessionAddedCount, setSessionAddedCount] = useState(0);

    // Reconcile session ownership whenever the polygon set changes: adopt
    // whatever is genuinely new since the last pass (but only if we were the
    // ones who asked for an add), and drop anything since deleted by any route.
    useEffect(() => {
        const owned = sessionKeysRef.current;
        const known = knownIdentitiesRef.current;
        const current = polygons.map(zoneIdentity);
        const currentSet = new Set(current.filter(Boolean));

        if (pendingAddRef.current) {
            current.forEach((id) => {
                if (id && !known.has(id)) owned.add(id);
            });
        }
        // Always spend the flag, adopted or not. Leaving it set would let a
        // later unrelated update — a vertex edit produces a new array — adopt a
        // pre-existing zone, which is the data-loss path all over again.
        pendingAddRef.current = false;

        owned.forEach((k) => { if (!currentSet.has(k)) owned.delete(k); });
        knownIdentitiesRef.current = currentSet;
        setSessionAddedCount(owned.size);
    }, [polygons]);

    const undoLastZone = useCallback(() => {
        if (!onPolygonRemoveRef.current) return;
        // Last zone this session actually created — never a pre-existing one.
        for (let i = polygons.length - 1; i >= 0; i--) {
            const id = zoneIdentity(polygons[i]);
            if (id && sessionKeysRef.current.has(id)) {
                sessionKeysRef.current.delete(id);
                setSessionAddedCount(sessionKeysRef.current.size);
                onPolygonRemoveRef.current(i);
                return;
            }
        }
    }, [polygons]);

    // --- Freehand tracing ----------------------------------------------------

    // Google's own pan/zoom must be off *before* the first touch lands, not on
    // touchstart: once a drag gesture has been claimed by the map it cannot be
    // taken back mid-gesture, and the stroke would pan the map instead.
    const setMapGesturesEnabled = useCallback((enabled) => {
        if (!rawMap) return;
        rawMap.setOptions({ gestureHandling: enabled ? 'auto' : 'none' });
    }, [rawMap]);

    // Single choke point for ending a stroke. Everything scoped to ONE stroke
    // is cleared here — the points and the owning pointer. Leaving the owner
    // behind let it leak into the next stroke, which is only harmless today by
    // accident (the next pointerdown overwrites it) and would bite the moment
    // that assignment moved or gained a condition.
    const resetStroke = useCallback(() => {
        strokePointsRef.current = [];
        strokeOwnerRef.current = null;
        setStrokePath('');
    }, []);

    // --- Camera / projection helpers -----------------------------------------

    // Map rect, and the pixel offset of the map's centre within it. All the
    // world-coordinate maths below is expressed relative to this point.
    const getMapMetrics = useCallback(() => {
        const div = rawMap?.getDiv();
        const projection = rawMap?.getProjection();
        const rect = div?.getBoundingClientRect();
        const center = rawMap?.getCenter();
        const zoom = rawMap?.getZoom();
        // getProjection() is undefined until the map has drawn once, and
        // getCenter() is undefined before the first camera is applied.
        if (!rect || !projection || !center || typeof zoom !== 'number') return null;

        const centerWorld = projection.fromLatLngToPoint(center);
        if (!centerWorld) return null;

        return {
            rect,
            projection,
            zoom,
            centerWorld,
            scale: Math.pow(2, zoom),
            halfWidth: rect.width / 2,
            halfHeight: rect.height / 2
        };
    }, [rawMap]);

    // Recompute every stroke point's screen position from its world coordinate
    // and rebuild the SVG path, so the drawn line stays glued to the ground
    // while the camera moves under it.
    //
    // `camera` lets the pinch pass in the frame it just applied. Re-reading the
    // map instead would risk picking up the pre-move camera if Google hasn't
    // propagated moveCamera yet, and the preview would trail a frame behind the
    // fingers.
    const reprojectStroke = useCallback((camera) => {
        const pts = strokePointsRef.current;
        if (pts.length === 0) return;
        const m = camera || getMapMetrics();
        if (!m) return;

        for (const pt of pts) {
            pt.x = (pt.wx - m.centerWorld.x) * m.scale + m.halfWidth;
            pt.y = (pt.wy - m.centerWorld.y) * m.scale + m.halfHeight;
        }
        setStrokePath(pts.map((p) => `${p.x},${p.y}`).join(' '));
    }, [getMapMetrics]);

    const startFreehand = useCallback(() => {
        if (!rawMap) return;
        resetStroke();
        setStrokeError('');
        // Force the camera flat and north-up before any capture happens. The
        // reprojection is axis-aligned Mercator, so a camera the user rotated
        // or tilted earlier would skew the previewed line away from their
        // finger. Cheap, and a no-op on the overwhelmingly common path.
        if (rawMap.getTilt?.() || rawMap.getHeading?.()) {
            rawMap.setTilt?.(0);
            rawMap.setHeading?.(0);
        }
        setMapGesturesEnabled(false);
        setDrawingMode(FREEHAND_MODE);
    }, [rawMap, resetStroke, setMapGesturesEnabled]);

    const exitFreehand = useCallback(() => {
        resetStroke();
        activePointersRef.current.clear();
        pinchRef.current = null;
        setMapGesturesEnabled(true);
        if (drawRef.current) drawRef.current.setMode(IDLE_MODE);
        setDrawingMode(IDLE_MODE);
    }, [resetStroke, setMapGesturesEnabled]);

    // Turn the traced screen path into a saved zone. Everything that can reject
    // the stroke happens here, before the parent ever sees it.
    const commitStroke = useCallback(() => {
        const rawPoints = strokePointsRef.current;
        strokePointsRef.current = [];
        setStrokePath('');

        // Every rejection below must say something. A stroke that just vanishes
        // reads as a broken app, not as "try again".
        if (rawPoints.length < 3) {
            setStrokeError('That was too short to be an area — draw around the streets you want.');
            return false;
        }

        // Measure the shape in the frame the drawing actually HAPPENED in: the
        // median of the per-point capture scales.
        //
        // All three gates below (RDP tolerance, self-intersection, minimum area)
        // are tuned in screen pixels, and `x`/`y` track the live camera, so once
        // pinching exists no single endpoint frame works:
        //   - end scale:   trace a neighbourhood, zoom OUT to see it whole, and
        //                  the area shrinks 4^levels — three levels turns
        //                  90,000px² into 1,400px² → "too small".
        //   - start scale: the map opens at zoom 4, so the ordinary flow is to
        //                  start, pinch IN to street level, then trace. Measured
        //                  back at 2^4 that shape collapses past RDP → "too
        //                  short to be an area".
        //   - max scale:   fixes both area cases, but silently breaks the
        //                  TOLERANCE. Points are sampled 3px apart in their own
        //                  capture frame; judged in a frame k levels larger they
        //                  sit 3·2^k apart against a 2px tolerance, so RDP
        //                  removes nothing and hand tremor survives — which is
        //                  exactly what makes ringSelfIntersects fire. The
        //                  false-reject just moves from "too small" to "crosses
        //                  itself".
        //
        // The median is where the pen actually was for most of the stroke, so a
        // 2px tolerance means 2px-as-drawn and the area is the area the user saw
        // while tracing. With no pinch every capture scale is identical and this
        // reduces exactly to the old behaviour. It also keeps coordinates near
        // the drawing frame rather than inflating them, which matters for the
        // absolute epsilon inside the self-intersection test.
        //
        // Offset by the first point: all three gates are translation-invariant,
        // and it keeps the shoelace sum well inside float precision.
        const captureScales = rawPoints.map((pt) => pt.cs).filter((s) => Number.isFinite(s) && s > 0);
        const origin = rawPoints[0];
        if (captureScales.length !== rawPoints.length || !origin) {
            // Only reachable if the projection was unavailable at capture time.
            // Judging the shape by the live x/y instead would measure it in
            // whatever frame the last pinch left behind — the exact bug this
            // block exists to prevent — so refuse rather than guess.
            setStrokeError('The map is still loading — give it a second and try again.');
            return false;
        }
        captureScales.sort((a, b) => a - b);
        const scale = captureScales[Math.floor(captureScales.length / 2)];
        const raw = rawPoints.map((pt) => ({
            x: (pt.wx - origin.wx) * scale,
            y: (pt.wy - origin.wy) * scale,
            lat: pt.lat,
            lng: pt.lng
        }));

        // Budgeted, not a plain simplify: the frame above may be pinch-inflated,
        // which defeats a fixed 2px tolerance. An ordinary no-pinch stroke lands
        // around 40 points on the first pass and is returned untouched.
        const ring = simplifyToVertexBudget(raw, STROKE_SIMPLIFY_PX, STROKE_MAX_VERTICES);
        if (ring.length < 3) {
            setStrokeError('That was too short to be an area — draw around the streets you want.');
            return false;
        }

        // Self-intersection BEFORE area, deliberately. The shoelace formula sums
        // signed areas, so a figure-eight's lobes cancel and it measures as
        // near-zero — checking area first would tell the user their shape was
        // "too small" when the real problem is that it crosses itself.
        if (ringSelfIntersects(ring)) {
            setStrokeError('That shape crosses itself — try drawing it again without overlapping.');
            return false;
        }
        if (ringAreaPx(ring) < STROKE_MIN_AREA_PX) {
            setStrokeError('That shape was too small — try drawing a larger area.');
            return false;
        }

        // Coordinates were resolved at capture time, not here — that is what
        // lets the camera move mid-stroke without invalidating the shape.
        const coordinates = [];
        for (const pt of ring) {
            const lng = roundCoord(pt.lng);
            const lat = roundCoord(pt.lat);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
            // Rounding can collapse neighbouring points onto each other when the
            // map is zoomed far out; duplicate consecutive vertices would fail
            // TerraDraw's own geometry validation.
            const prev = coordinates[coordinates.length - 1];
            if (prev && prev[0] === lng && prev[1] === lat) continue;
            coordinates.push([lng, lat]);
        }
        // Same collapse risk between the last point and the first, which the
        // closing step below would otherwise turn into a degenerate ring.
        while (
            coordinates.length > 1 &&
            coordinates[0][0] === coordinates[coordinates.length - 1][0] &&
            coordinates[0][1] === coordinates[coordinates.length - 1][1]
        ) {
            coordinates.pop();
        }
        if (coordinates.length < 3) {
            setStrokeError("That shape couldn't be placed on the map — try drawing it again.");
            return false;
        }

        // Close the ring. Users never join the ends themselves, and GeoJSON
        // requires first === last.
        coordinates.push([...coordinates[0]]);

        setStrokeError('');
        onPolygonAddRef.current({
            name: `Zone ${polygonsCountRef.current + 1}`,
            active: true,
            geometry: { type: 'Polygon', coordinates: [coordinates] }
        });
        pendingAddRef.current = true;
        return true;
    }, []);

    // Turn a container-pixel position into a full stroke point. One projection
    // call, made once per appended point rather than once per pointermove.
    const makeStrokePoint = useCallback((x, y) => {
        const overlayProjection = projectionOverlayRef.current?.getProjection();
        const mapProjection = rawMap?.getProjection();
        if (!overlayProjection || !mapProjection) return null;

        const latLng = overlayProjection.fromContainerPixelToLatLng(
            new window.google.maps.Point(x, y)
        );
        if (!latLng) return null;
        const world = mapProjection.fromLatLngToPoint(latLng);
        if (!world) return null;

        // `cs` is the scale this point was CAPTURED at. Read straight off the
        // zoom rather than via getMapMetrics, which forces a layout read this
        // hot path doesn't need. commitStroke uses it to reconstruct the frame
        // the drawing actually happened in.
        const zoom = rawMap.getZoom();
        if (typeof zoom !== 'number') return null;

        return {
            x,
            y,
            lat: latLng.lat(),
            lng: latLng.lng(),
            wx: world.x,
            wy: world.y,
            cs: Math.pow(2, zoom)
        };
    }, [rawMap]);

    // Capture the frame a pinch starts from: finger separation, zoom, and the
    // world point under the midpoint that every later frame re-centres on.
    //
    // Called both when a second finger lands AND lazily on move, because fingers
    // do not go down and up in tidy pairs. Lifting one of three fingers leaves
    // two still down with no armed pinch; without re-arming there, the gesture
    // would go dead until the user lifted everything and started over.
    const armPinch = useCallback(() => {
        const m = getMapMetrics();
        const entries = [...activePointersRef.current.entries()];
        if (!m || entries.length < 2) {
            pinchRef.current = null;
            return false;
        }

        const [[idA, a], [idB, b]] = entries;
        const midX = (a.x + b.x) / 2;
        const midY = (a.y + b.y) / 2;
        pinchRef.current = {
            // Which two fingers this baseline belongs to. If the participating
            // PAIR changes — a third finger lands, or one of these two lifts —
            // startDist refers to a separation that no longer exists, and the
            // next frame's zoom would jump by an arbitrary amount. Callers
            // compare against this and re-arm rather than reusing a stale pair.
            idA,
            idB,
            startDist: Math.max(Math.hypot(b.x - a.x, b.y - a.y), 1),
            startZoom: m.zoom,
            // The world point under the fingers at gesture start. Every frame
            // then re-centres so this stays put beneath them, which is what
            // makes the pinch feel anchored rather than sliding.
            anchorWx: (midX - m.halfWidth) / m.scale + m.centerWorld.x,
            anchorWy: (midY - m.halfHeight) / m.scale + m.centerWorld.y
        };
        return true;
    }, [getMapMetrics]);

    const handleStrokeStart = useCallback((event) => {
        if (drawingMode !== FREEHAND_MODE) return;

        const rect = rawMap?.getDiv()?.getBoundingClientRect();
        if (!rect) return;
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        activePointersRef.current.set(event.pointerId, { x, y });
        // Capture so a finger that slides off the map still routes here.
        try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* not fatal */ }

        // Second finger down: reframe, don't draw. The stroke is NOT discarded —
        // its points carry their own geography, so the camera can move
        // underneath them and they stay over the same streets. Freeze the
        // tracing hand and capture the pinch's starting frame.
        if (activePointersRef.current.size > 1) {
            armPinch();
            return;
        }

        setStrokeError('');
        strokeOwnerRef.current = event.pointerId;

        const point = makeStrokePoint(x, y);
        // Projections are undefined until Google has drawn once, which a fast
        // tap-and-trace on a slow connection can beat. Say so here: capture now
        // happens on the way down, so without this the whole stroke would
        // silently record nothing and simply vanish on lift.
        if (!point) {
            strokePointsRef.current = [];
            setStrokeError('The map is still loading — give it a second and try again.');
            return;
        }
        strokePointsRef.current = [point];
    }, [drawingMode, rawMap, makeStrokePoint, armPinch]);

    const handleStrokeMove = useCallback((event) => {
        if (drawingMode !== FREEHAND_MODE) return;

        const rect = rawMap?.getDiv()?.getBoundingClientRect();
        if (!rect) return;
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        const pointers = activePointersRef.current;
        if (!pointers.has(event.pointerId)) return;
        pointers.set(event.pointerId, { x, y });

        // --- Two fingers: drive the camera ourselves --------------------------
        // Google's own gestureHandling is 'none' while freehand is armed, and
        // this layer owns every pointer, so there is nothing to hand the pinch
        // to — we compute the camera directly. The maths is done in world
        // coordinates rather than by re-reading the projection each frame, so it
        // never depends on whether Google has redrawn yet.
        if (pointers.size > 1) {
            const entries = [...pointers.entries()];
            const [[idA, a], [idB, b]] = entries;
            // Re-baseline whenever there is no armed pinch, or the two fingers
            // driving it are no longer the two it was armed on. Both happen
            // routinely: a third finger brushes the glass, or one of the
            // original pair lifts. Without this the gesture either goes dead or
            // snaps to a wild zoom computed from a separation that no longer
            // exists. Re-arming costs one frame and always recovers.
            const pinch = pinchRef.current;
            if (!pinch || pinch.idA !== idA || pinch.idB !== idB) {
                armPinch();
                return;
            }

            const m = getMapMetrics();
            if (!m) return;

            const dist = Math.max(Math.hypot(b.x - a.x, b.y - a.y), 1);
            const midX = (a.x + b.x) / 2;
            const midY = (a.y + b.y) / 2;

            const rawZoom = pinch.startZoom + Math.log2(dist / pinch.startDist);
            // The load-bearing half is `pinch.startZoom`: a fixed ceiling of 20
            // would yank the camera outward the instant a second finger landed
            // on someone already at street-level zoom 21, before they had moved
            // at all. The get() calls are belt-and-braces — nothing sets
            // minZoom/maxZoom on this map today, so they return undefined and
            // fall through to the constants.
            const maxZoom = Math.max(rawMap.get('maxZoom') ?? MAX_PINCH_ZOOM, pinch.startZoom);
            const minZoom = Math.min(rawMap.get('minZoom') ?? MIN_PINCH_ZOOM, pinch.startZoom);
            const nextZoom = Math.min(Math.max(rawZoom, minZoom), maxZoom);
            const nextScale = Math.pow(2, nextZoom);

            // Re-centre so the anchor world point lands back under the fingers.
            const centerWorld = new window.google.maps.Point(
                pinch.anchorWx - (midX - m.halfWidth) / nextScale,
                pinch.anchorWy - (midY - m.halfHeight) / nextScale
            );
            const center = m.projection.fromPointToLatLng(centerWorld);
            if (!center) return;

            if (typeof rawMap.moveCamera === 'function') {
                rawMap.moveCamera({ center, zoom: nextZoom });
                // Reproject against the camera we just applied, not the one the
                // map reports back.
                reprojectStroke({
                    centerWorld,
                    scale: nextScale,
                    halfWidth: m.halfWidth,
                    halfHeight: m.halfHeight
                });
            } else {
                // No moveCamera on very old API builds. Two calls instead of
                // one, and setZoom only honours integers — so recompute the
                // centre for the zoom that will ACTUALLY be applied. Reusing the
                // fractional-zoom centre here would drift the anchor out from
                // under the fingers and desync the preview from the map.
                const appliedZoom = Math.round(nextZoom);
                const appliedScale = Math.pow(2, appliedZoom);
                const appliedCenterWorld = new window.google.maps.Point(
                    pinch.anchorWx - (midX - m.halfWidth) / appliedScale,
                    pinch.anchorWy - (midY - m.halfHeight) / appliedScale
                );
                const appliedCenter = m.projection.fromPointToLatLng(appliedCenterWorld);
                if (!appliedCenter) return;
                rawMap.setZoom(appliedZoom);
                rawMap.setCenter(appliedCenter);
                reprojectStroke({
                    centerWorld: appliedCenterWorld,
                    scale: appliedScale,
                    halfWidth: m.halfWidth,
                    halfHeight: m.halfHeight
                });
            }
            return;
        }

        // --- One finger: trace ------------------------------------------------
        if (pointers.size !== 1) return;
        // Only the finger that began the stroke may extend it. Coming out of a
        // pinch it can be the OTHER finger left on the glass, and resuming from
        // that one appends a long straight segment from the last point to
        // wherever it happens to be — usually across the middle of the shape,
        // which then trips the self-intersection check.
        if (event.pointerId !== strokeOwnerRef.current) return;
        const pts = strokePointsRef.current;
        if (pts.length === 0) return;

        const last = pts[pts.length - 1];
        if (Math.hypot(x - last.x, y - last.y) < STROKE_MIN_STEP_PX) return;

        const point = makeStrokePoint(x, y);
        if (!point) return;
        pts.push(point);
        setStrokePath(pts.map((p) => `${p.x},${p.y}`).join(' '));
    }, [drawingMode, rawMap, getMapMetrics, makeStrokePoint, reprojectStroke, armPinch]);

    const handleStrokeEnd = useCallback((event) => {
        const wasOwner = event.pointerId === strokeOwnerRef.current;
        activePointersRef.current.delete(event.pointerId);
        try { event.currentTarget.releasePointerCapture(event.pointerId); } catch { /* already released */ }

        if (drawingMode !== FREEHAND_MODE) return;

        // A PINCH finger lifted and the drawing hand is still down: the stroke
        // continues under its owner. This is the intended resume path.
        if (!wasOwner && activePointersRef.current.size > 0) {
            pinchRef.current = null;
            return;
        }

        // Otherwise the owning finger has come up, and the stroke is finished —
        // even if a pinch finger is still resting on the glass.
        //
        // Do NOT hand the stroke to that survivor. An earlier version did, to
        // cure a dead screen (the survivor couldn't draw and the map was frozen
        // because gestures are off while armed). But the survivor sits wherever
        // the pinch left it, usually well away from the line, so its first move
        // appended a long straight segment across the shape. When that segment
        // happened not to self-intersect it closed into a perfectly plausible
        // ring covering ground the user never traced, and committed silently
        // into their buy-box. Ending here fixes the dead screen too, and keeps
        // the file's existing contract that a lift completes the shape.
        pinchRef.current = null;

        // A bare tap is orientation, not a failed drawing. Reset quietly rather
        // than accusing the user of drawing something too small.
        if (strokePointsRef.current.length < 3) {
            resetStroke();
            return;
        }

        // Lift completes the shape and disarms — one stroke per arming, so a
        // stray finger can't keep scribbling new zones.
        const saved = commitStroke();
        if (saved) {
            exitFreehand();
        } else {
            // Stay armed on rejection so the retry is a single gesture, not a
            // re-tap of the toolbar.
            resetStroke();
        }
    }, [drawingMode, commitStroke, exitFreehand, resetStroke]);

    // Drop out of freehand into TerraDraw's tap-to-place mode, restoring the
    // map's own gestures (tap-to-place needs no custom capture layer).
    const switchToTapMode = useCallback(() => {
        resetStroke();
        activePointersRef.current.clear();
        pinchRef.current = null;
        setStrokeError('');
        setMapGesturesEnabled(true);
        if (drawRef.current) {
            drawRef.current.setMode('polygon');
            setDrawingMode('polygon');
        }
    }, [resetStroke, setMapGesturesEnabled]);

    // pointercancel means the OS or browser took the gesture away (a system edge
    // swipe, an incoming call, a scroll claim) — the user never finished the
    // shape. Committing here would persist a half-traced arc closed into a zone,
    // so discard instead.
    const handleStrokeCancel = useCallback((event) => {
        try { event.currentTarget.releasePointerCapture(event.pointerId); } catch { /* already released */ }
        // Clear EVERY pointer, not just the cancelled one. iOS fires
        // pointercancel readily, and deleting one of a pair leaves the other
        // stranded in the Map with no armed pinch — a state where two fingers
        // are on the glass and nothing responds. The stroke is being discarded
        // regardless, so there is nothing left for the survivor to contribute.
        const hadStroke = strokePointsRef.current.length >= 3;
        activePointersRef.current.clear();
        pinchRef.current = null;
        resetStroke();
        // Say something if there was a real shape on screen. Everywhere else in
        // this file a stroke that vanishes without a word is treated as reading
        // like a broken app; this path was the one exception.
        if (hadStroke) {
            setStrokeError('That got interrupted — draw your area again.');
        }
    }, [resetStroke]);

    // Restore Google's gestures if we unmount mid-draw.
    useEffect(() => () => {
        if (rawMap) rawMap.setOptions({ gestureHandling: 'auto' });
    }, [rawMap]);

    // Clear a rejection message after a beat so it doesn't linger over the map.
    useEffect(() => {
        if (!strokeError) return;
        const timer = setTimeout(() => setStrokeError(''), 4000);
        return () => clearTimeout(timer);
    }, [strokeError]);

    // Same for a failed search — it sits above the map, and a stale "couldn't
    // find that place" next to a box the user has since retyped is misleading.
    useEffect(() => {
        if (!searchError) return;
        const timer = setTimeout(() => setSearchError(''), 5000);
        return () => clearTimeout(timer);
    }, [searchError]);

    if (!isMapLoaded) {
        return null;
    }

    const isFreehand = drawingMode === FREEHAND_MODE;
    const isDrawing = isFreehand || drawingMode === 'polygon';

    return (
        <div className="buyer-zone-map-shell">
            {/* One-time onboarding copy. Deliberately ABOVE the map, not overlaid
                on it: on a phone the canvas is the scarce resource, and a pill
                floating over it cost ~40px of the little drawing room there was.

                Gated on zone count ONLY, never on isDrawing — the text swaps but
                the line stays. Unmounting it as drawing starts would resize the
                map container at the exact moment tracing begins, and a projection
                that hasn't caught up with its new height shifts every captured
                point. Keeping the height constant removes that class of bug
                rather than relying on Google to reflow in time. */}
            {polygons.length === 0 && (
                <div className="buyer-zone-empty-hint body-regular">
                    {isDrawing
                        ? 'Draw around the area you buy in.'
                        : <>Tap <span className="body-semibold">+ Draw a zone</span>, then draw around your area.</>}
                </div>
            )}

            <div className={`buyer-zone-map-container${isDrawing ? ' is-drawing' : ''}${!isDrawing && searchError ? ' has-search-error' : ''}`}>
            <GoogleMap
                zoom={4}
                center={defaultCenter}
                mapContainerClassName="buyer-zone-map-element"
                options={mapOptions}
                onLoad={handleMapLoad}
            >
                {rawMap && (
                    <MapModeToggle
                        toggleSatellite={toggleSatellite}
                        mapType={mapType}
                    />
                )}

                {/* Numbered badge on each zone's centroid — direct labelling so
                    a zone and its chip ("Zone 1") read as the same object. Hover
                    or tap a badge to light up its chip (reciprocal highlight). */}
                {rawMap && polygons.map((poly, i) => {
                    const center = getPolygonCenter(poly.geometry);
                    // Skip if no/degenerate geometry (e.g. unparsed string from
                    // some callers, or a malformed ring) — a non-finite centroid
                    // would otherwise place a stray badge.
                    if (!center || !Number.isFinite(center.lat) || !Number.isFinite(center.lng)) return null;
                    const active = i === hoveredZoneIndex || i === focusedZoneIndex;
                    return (
                        <Marker
                            key={poly._key ?? `zone-badge-${i}`}
                            map={rawMap}
                            position={center}
                            zIndex={active ? 20 : 10}
                        >
                            <div
                                className={`buyer-zone-badge xs-label-bold${active ? ' is-active' : ''}`}
                                onMouseEnter={() => onZoneHover && onZoneHover(i)}
                                onMouseLeave={() => onZoneHover && onZoneHover(null)}
                                onClick={() => onZoneHover && onZoneHover(i)}
                            >
                                {i + 1}
                            </div>
                        </Marker>
                    );
                })}
            </GoogleMap>

            {/* Freehand capture layer. Only mounted while tracing, because
                `touch-action: none` left on permanently creates a dead zone
                where the map stops responding to touch at all. */}
            {isFreehand && (
                <div
                    className="buyer-zone-freehand-layer"
                    onPointerDown={handleStrokeStart}
                    onPointerMove={handleStrokeMove}
                    onPointerUp={handleStrokeEnd}
                    onPointerCancel={handleStrokeCancel}
                >
                    {strokePath && (
                        <svg className="buyer-zone-freehand-svg">
                            <polyline className="buyer-zone-freehand-path" points={strokePath} />
                        </svg>
                    )}
                </div>
            )}

            {/* Place search. Hidden mid-draw: this is framing UI, and mid-stroke
                it would only eat canvas the user is actively drawing on. */}
            {!isDrawing && (
                <form className="buyer-zone-search" role="search" onSubmit={handleSearch}>
                    <input
                        type="text"
                        className="buyer-zone-search-input body-regular"
                        placeholder="Search a city or ZIP"
                        aria-label="Search for a city or ZIP to move the map"
                        inputMode="search"
                        enterKeyHint="search"
                        autoComplete="off"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <button
                        type="submit"
                        className="buyer-zone-search-button body-semibold"
                        disabled={searching || !searchQuery.trim()}
                    >
                        {searching ? 'Finding…' : 'Go'}
                    </button>
                </form>
            )}

            {/* Search failures sit directly under the box that caused them.
                role=alert so a screen reader hears them at all — the map gives
                no other signal that nothing moved. */}
            {!isDrawing && searchError && (
                <div className="buyer-zone-search-error body-regular" role="alert">
                    {searchError}
                </div>
            )}

            {/* Drawing toolbar. Mid-draw this becomes a Cancel | Done pair so the
                mode is unmistakable and has an explicit exit — see the tint
                overlay, which makes draw mode visible rather than a silent state
                change that invites mode-slip errors. */}
            <div className="buyer-zone-toolbar">
                {!isDrawing && (
                    <>
                        <button
                            className="buyer-zone-toolbar-button buyer-zone-toolbar-button-active body-semibold"
                            onClick={coarsePointer ? startFreehand : startDrawing}
                        >
                            + Draw a zone
                        </button>
                        {sessionAddedCount > 0 && polygons.length > 0 && (
                            <button
                                className="buyer-zone-toolbar-button body-semibold"
                                onClick={undoLastZone}
                            >
                                Undo
                            </button>
                        )}
                    </>
                )}

                {drawingMode === 'polygon' && (
                    <>
                        <button
                            className="buyer-zone-toolbar-button buyer-zone-toolbar-button-cancel body-semibold"
                            onClick={cancelDrawing}
                        >
                            Cancel
                        </button>
                        <button
                            className="buyer-zone-toolbar-button buyer-zone-toolbar-button-active body-semibold"
                            onClick={finishDrawing}
                        >
                            Done
                        </button>
                    </>
                )}

                {isFreehand && (
                    <>
                        <button
                            className="buyer-zone-toolbar-button buyer-zone-toolbar-button-cancel body-semibold"
                            onClick={exitFreehand}
                        >
                            Cancel
                        </button>
                        {/* Precision path, and the accessibility path: freehand
                            alone is unusable for anyone who can't drag accurately. */}
                        <button
                            className="buyer-zone-toolbar-button body-semibold"
                            onClick={switchToTapMode}
                        >
                            Tap to place
                        </button>
                    </>
                )}
            </div>

            {/* Instructions, and the rejection message when a stroke is thrown
                out. Copy differs by pointer type — "click" and a visible cursor
                make no sense on a phone. */}
            {(isDrawing || strokeError) && (
                <div className={`buyer-zone-drawing-instructions body-regular${strokeError ? ' is-error' : ''}`}>
                    {strokeError || (isFreehand
                        ? 'Draw with one finger. Pinch with two to zoom.'
                        : (coarsePointer
                            ? 'Tap each corner, then tap Done.'
                            : 'Click corners, then click the first point (or press Enter) to close.'))}
                </div>
            )}
            </div>
        </div>
    );
}

export default React.memo(BuyerZoneDrawMap);
